import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Paper, Avatar, AvatarGroup, TextField, Button, Divider, useTheme, Select, MenuItem, FormControl, InputLabel, IconButton, Dialog, DialogContent, DialogActions, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import firebase from '../../utilities/firebase';
import Store from '../../utilities/store';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PrintIcon from '@mui/icons-material/Print';
import LoadSelector from './LoadSelector';
import { useStackZIndex, ModalStackProvider, useDatePickerSlotProps } from '../../utilities/ModalStackContext';
import ChecklistDialog from './ChecklistDialog';
import AddOutsideCarForm from './AddOutsideCarForm';
import FinancialDetails from './FinancialDetails';
import VehicleDetailsDialog from './VehicleDetailsDialog';
import VehiclesSection from './VehiclesSection';
import RouteSection from './RouteSection';


const driverCostPerMile = {
  'Scott Bivens': 0.63,
  'Clarence Bowens': 0.65,
  'Kelly Lindberg': 0.55,
  'Kelly Lindberg - New': 0.60,
  'Steve McIntosh': 0.50,
  'Porters': 0.00,
  'Outside Vendor': 0.00
}


const ShippingLoadContent = ({ loadId, onDeleted }) => {
  const myZ = useStackZIndex();
  const datePickerSlotProps = useDatePickerSlotProps(myZ);
  const [loadData, setLoadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markers, setMarkers] = useState([]);
  const [isLoadSelectorOpen, setIsLoadSelectorOpen] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [isAddOutsideCarFormOpen, setIsAddOutsideCarFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRouteUpdating, setIsRouteUpdating] = useState(false);

  const [startLocation, setStartLocation] = useState(null);
  const [endLocation, setEndLocation] = useState(null);
  const isInitialLoad = useRef(true);



  useEffect(() => {
    const fetchLoadData = async () => {
      StateManager.setLoading(true);
      setLoading(true);
      setLoadData(null);
      isInitialLoad.current = true;
      try {
        const loadDoc = await firebase.firestore().collection('shipping-loads').doc(loadId).get();

        if (loadDoc.exists) {
          const data = loadDoc.data();
          setLoadData(data);
          const newMarkers = data.cars
            .filter(car => car?.location?.lat && car?.location?.lng)
            .map(car => ({
              position: {
                lat: car.location.lat,
                lng: car.location.lng
              },
              car: car
            }));
          setMarkers(newMarkers);
        } else {
          StateManager.setAlertAndOpen("Error fetching load details", "error");
        }
      } catch (error) {
        console.error('Error fetching load details:', error);
        StateManager.setAlertAndOpen("Error fetching load details", "error");
      } finally {
        setLoading(false);
        StateManager.setLoading(false);
      }
    };

    if (loadId) {
      fetchLoadData();
    }
  }, [loadId]);

  useEffect(() => {
    if (mapInstance && markers && markers.length > 0 && window.google && window.google.maps) {
      if (markers.length === 1) {
        mapInstance.panTo(markers[0].position);
        mapInstance.setZoom(14);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach(marker => {
          bounds.extend(marker.position);
        });
        mapInstance.fitBounds(bounds);
      }
    }
  }, [mapInstance, markers]);

  useEffect(() => {
    if (!loadData) return;
    const geocodeAddress = async (address) => {
      if (!address || !window.google || !window.google.maps || !window.google.maps.Geocoder) return null;

      const geocoder = new window.google.maps.Geocoder();
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0].geometry.location);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          });
        });
        return {
          lat: result.lat(),
          lng: result.lng()
        };
      } catch (error) {
        console.error('Geocoding error:', error);
        return null;
      }
    };

    const updateLocations = async () => {
      if (loadData?.start) {
        const startLoc = await geocodeAddress(loadData.start);
        setStartLocation(startLoc);
      } else {
        setStartLocation(null);
      }

      if (loadData?.end) {
        const endLoc = await geocodeAddress(loadData.end);
        setEndLocation(endLoc);
      } else {
        setEndLocation(null);
      }

      if (!isInitialLoad.current) {
        await handleAddressChange();
      } else {
        isInitialLoad.current = false;
      }
    };

    updateLocations();
  }, [loadData?.start, loadData?.end]);

  const handleUpdateField = async (fieldName, fieldValue, options = {}) => {
    if (!loadId) {
      StateManager.setAlertAndOpen("Load ID is missing. Cannot update field.", "error");
      return;
    }

    const { carId } = options;
    let updatedLoadData = { ...loadData };
    let dataToUpdate = {};

    if (fieldName === 'charge' && carId) {
      updatedLoadData.cars = (updatedLoadData.cars || []).map(car =>
        car.id === carId ? { ...car, charge: parseFloat(fieldValue) || 0 } : car
      );
      dataToUpdate.cars = updatedLoadData.cars;
    } else if (fieldName === 'driver') {
      updatedLoadData.driver = fieldValue;
      dataToUpdate.driver = fieldValue;
      const costPerMile = driverCostPerMile[fieldValue] || 0;
      updatedLoadData.cost_per_mile = costPerMile;
      dataToUpdate.cost_per_mile = costPerMile;
    } else if (fieldName === 'cars' && carId && options.updates) {
      updatedLoadData.cars = (updatedLoadData.cars || []).map(car =>
        car.id === carId ? {   ...car, ...options.updates } : car
      );
      dataToUpdate.cars = updatedLoadData.cars;
    } else {
      updatedLoadData[fieldName] = fieldValue;
      dataToUpdate[fieldName] = fieldValue;
    }

    setLoadData(updatedLoadData);

    try {
      const loadDocRef = firebase.firestore().collection('shipping-loads').doc(loadId);
      await loadDocRef.update(dataToUpdate);
      Store.invalidateShippingLoads(); // derived car shipping status reads this
    } catch (error) {
      console.error(`Error updating ${fieldName} in Firebase:`, error);
      StateManager.setAlertAndOpen(`Error updating ${fieldName}. Please try again.`, "error");
    }
  };

  const handleAddStops = () => {
    setIsLoadSelectorOpen(true);
  };

  const handleLoadSelectorClose = () => {
    setIsLoadSelectorOpen(false);
  };

  const handleLoadSelected = async (carsForLoad) => {
    handleLoadSelectorClose();

    const existingCharges = {};
    if (loadData?.cars) {
      loadData.cars.forEach(car => {
        if (['car_delivery', 'car_pickup'].includes(car.type) && car.charge) {
          existingCharges[car.id] = car.charge;
        }
      });
    }

    const carsWithPreservedCharges = carsForLoad.map(car => ({
      ...car,
      charge: existingCharges[car.id] || car.charge || 0
    }));

    await handleNewStops(carsWithPreservedCharges);
  }

  const handleNewStops = async (newStops, reset = false, optimizeWaypoints = false) => {
    handleLoadSelectorClose();
    setIsRouteUpdating(true);

    const stops = reset ? newStops : [...new Map([...(loadData?.cars || []), ...newStops].map(car => [car.id, car])).values()];

    try {
      const response = await RequestManager.post({
        function: 'addCarsToLoad',
        variables: {
          loadId,
          stops,
          optimizeWaypoints
        }
      });

      if (response.success) {
        Store.invalidateShippingLoads(); // cars[] changed → refresh derived status
        setLoadData(response.load);
        const newMarkers = response.load.cars
          .filter(car => car?.location?.lat && car?.location?.lng)
          .map(car => ({
            position: {
              lat: car.location.lat,
              lng: car.location.lng
            },
            car: car
          }));
        setMarkers(newMarkers);
        StateManager.setAlertAndOpen("Stops updated successfully", "success");
      } else {
        throw new Error("Failed to update stops");
      }
    } catch (error) {
      console.error('Error updating stops:', error);
      StateManager.setAlertAndOpen("Error updating stops", "error");
    } finally {
      setIsRouteUpdating(false);
    }
  };

  const handleOpenOutsideCarForm = () => {
    setIsAddOutsideCarFormOpen(true);
  };

  const handleCloseOutsideCarForm = () => {
    setIsAddOutsideCarFormOpen(false);
  };

  const handleOutsideCarSubmit = async (outsideCarData) => {
    if (!loadData) {
      StateManager.setAlertAndOpen("Load data not available. Cannot add outside car.", "error");
      return;
    }

    const newCarStop = {
      ...outsideCarData,
    };

    const updatedCars = [...(loadData.cars || []), newCarStop];

    const newOutsideStops = updatedCars.filter(car => car.type === 'outside_stop');

    try {
      await handleUpdateField('cars', updatedCars);
      await handleNewStops(newOutsideStops);
    } catch (error) {
      console.error("Error during outside car submission process:", error);
    }
  };

  const handleRemoveCarOrStop = async (itemIdToRemove) => {
    if (!loadData || !loadData.cars) {
      StateManager.setAlertAndOpen("Cannot remove item: Load data is missing.", "error");
      return;
    }

    const updatedCars = loadData.cars.filter(car => car.id !== itemIdToRemove);

    setLoadData(prevLoadData => ({
      ...prevLoadData,
      cars: updatedCars
    }));
    try {
      await handleNewStops(updatedCars, true);
    } catch (error) {
      console.error('Error after removing item and calling handleNewStops:', error);
    }
  };

  const handleDragEnd = async (updatedCars) => {
    if (!loadData || !loadData.cars) {
      StateManager.setAlertAndOpen("Cannot update order: Load data is missing.", "error");
      return;
    }

    try {
      await handleNewStops(updatedCars, true, false);
    } catch (error) {
      console.error('Error updating route after drag:', error);
      StateManager.setAlertAndOpen("Error updating route after reordering", "error");
    }
  };

  const handleUpdateOrderLocally = async (updatedCars) => {
    setLoadData(prevLoadData => ({
      ...prevLoadData,
      cars: updatedCars
    }));
    setIsRouteUpdating(true);
  };

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(null);

  const handleVehicleClick = async (car) => {
    setSelectedVehicle(car);
    setIsVehicleDialogOpen(true);
  };

  const handleAddressChange = async () => {
    if (!loadData || !loadData.cars) {
      StateManager.setAlertAndOpen("Cannot update route: Load data is missing.", "error");
      return;
    }

    if(loadData.cars.length === 0){
      console.log('No cars in load')
      return;
    }

    try {
      const loadDoc = await firebase.firestore().collection('shipping-loads').doc(loadId).get();
      if (loadDoc.exists) {
        const latestLoadData = loadDoc.data();
        await handleNewStops(latestLoadData.cars, true, false);
      } else {
        await handleNewStops(loadData.cars, true, false);
      }
    } catch (error) {
      console.error('Error updating route after address change:', error);
      StateManager.setAlertAndOpen("Error updating route after address change", "error");
    }
  };

  const handlePrint = () => {
    if (!loadData) return;

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const fmt = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';
    const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const statusLabel = { pending: 'Pending', posted: 'Posted', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' }[loadData.status] || loadData.status || '—';

    const revenue = (loadData.cars || []).reduce((t, c) => t + (c.charge || 0), 0);
    const driverCostVal = loadData.driver_pay_type === 'hourly'
      ? (parseFloat(loadData.hours_worked) || 0) * (parseFloat(loadData.hourly_rate) || 0)
      : (loadData.total_miles || 0) * (parseFloat(loadData.cost_per_mile) || 0);
    const additionalCost = loadData.additional_cost || 0;
    const profit = revenue - driverCostVal - additionalCost;
    const driverCostLabel = loadData.driver_pay_type === 'hourly'
      ? 'Driver Cost (' + (loadData.hours_worked || 0) + ' hrs @ $' + (loadData.hourly_rate || 0) + '/hr)'
      : 'Driver Cost ($' + (loadData.cost_per_mile || 0) + '/mi × ' + (loadData.total_miles || 0) + ' mi)';

    const carsHtml = (loadData.cars || []).map((car, i) => {
      const thumb = car.thumbnail
        ? '<img src="' + car.thumbnail + '" class="car-thumb" onerror="this.style.display=\'none\'" />'
        : '<div class="car-thumb-ph"></div>';
      const title = car.type === 'outside_stop'
        ? (car.carTitle || '—')
        : '#' + car.id + ' — ' + (car.carTitle || '');
      const chargeHtml = car.charge != null ? '<div class="car-charge">$' + money(car.charge) + '</div>' : '';
      const badgeCls = car.stopType === 'pickup' ? 'badge-pickup' : 'badge-dropoff';
      const badgeText = car.stopType === 'pickup' ? 'Pickup' : 'Dropoff';
      return '<div class="car-row">'
        + '<div class="car-num">' + (i + 1) + '</div>'
        + thumb
        + '<div class="car-body">'
        + '<div class="car-title">' + title + '</div>'
        + '<div class="car-sub">' + (car.customerName || '') + '</div>'
        + '<div class="car-sub">' + (car.address || '') + '</div>'
        + '</div>'
        + '<div class="car-right">'
        + '<span class="badge ' + badgeCls + '">' + badgeText + '</span>'
        + chargeHtml
        + '</div>'
        + '</div>';
    }).join('');

    const centerLat = mapInstance ? mapInstance.getCenter().lat() : (markers[0]?.position?.lat || 39.8283);
    const centerLng = mapInstance ? mapInstance.getCenter().lng() : (markers[0]?.position?.lng || -98.5795);
    const zoom = mapInstance ? mapInstance.getZoom() : 6;
    const markersJson = JSON.stringify(markers.map(m => m.position));
    const polylineJson = loadData.route_polyline ? JSON.stringify(loadData.route_polyline) : 'null';
    const startJson = startLocation ? JSON.stringify(startLocation) : 'null';
    const endJson = endLocation ? JSON.stringify(endLocation) : 'null';

    const html = '<!DOCTYPE html>\n<html>\n<head>\n'
      + '<meta charset="utf-8">\n'
      + '<title>Load #' + loadData.id + ' — Summary<\/title>\n'
      + '<style>\n'
      + '* { box-sizing: border-box; margin: 0; padding: 0; }\n'
      + 'body { font-family: Arial, Helvetica, sans-serif; color: #222; padding: 24px; font-size: 13px; }\n'
      + '.header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 2px solid #023e84; }\n'
      + '.header h1 { color: #023e84; font-size: 22px; }\n'
      + '.header .sub { font-size: 12px; color: #555; margin-top: 4px; }\n'
      + '.header .dates { text-align: right; font-size: 12px; color: #555; line-height: 1.7; }\n'
      + '.meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; background: #f5f7fa; padding: 14px; border-radius: 6px; }\n'
      + '.meta-item label { font-size: 10px; color: #888; display: block; text-transform: uppercase; letter-spacing: 0.5px; }\n'
      + '.meta-item span { font-size: 15px; font-weight: 700; }\n'
      + '.section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #023e84; margin-bottom: 10px; margin-top: 22px; padding-bottom: 4px; border-bottom: 1px solid #dde3ed; }\n'
      + '.car-row { display: flex; align-items: center; gap: 10px; padding: 9px 0; border-bottom: 1px solid #f0f0f0; }\n'
      + '.car-num { width: 24px; height: 24px; border-radius: 50%; background: #007AFF; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }\n'
      + '.car-thumb { width: 88px; height: 58px; object-fit: cover; border-radius: 4px; flex-shrink: 0; }\n'
      + '.car-thumb-ph { width: 88px; height: 58px; border-radius: 4px; background: #eee; flex-shrink: 0; }\n'
      + '.car-body { flex: 1; }\n'
      + '.car-title { font-weight: 600; font-size: 13px; }\n'
      + '.car-sub { color: #666; font-size: 11px; margin-top: 2px; }\n'
      + '.car-right { text-align: right; flex-shrink: 0; }\n'
      + '.badge { display: inline-block; font-size: 10px; padding: 2px 7px; border-radius: 3px; font-weight: 600; }\n'
      + '.badge-pickup { background: #e3f2fd; color: #0277bd; }\n'
      + '.badge-dropoff { background: #fce4ec; color: #c62828; }\n'
      + '.car-charge { font-weight: 700; font-size: 14px; margin-top: 5px; color: #1b5e20; }\n'
      + '#map { width: 100%; height: 320px; border-radius: 6px; border: 1px solid #ddd; margin-top: 8px; }\n'
      + '.fin-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 10px; }\n'
      + '.fin-item { background: #f5f7fa; padding: 12px 14px; border-radius: 6px; }\n'
      + '.fin-item label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }\n'
      + '.fin-item span { font-size: 16px; font-weight: 700; }\n'
      + '.green { color: #2e7d32; } .red { color: #c62828; }\n'
      + '@media print { body { padding: 12px; } #map { height: 280px; } }\n'
      + '<\/style>\n<\/head>\n<body>\n'
      + '<div class="header">'
      + '<div><h1>Load #' + loadData.id + '<\/h1>'
      + '<div class="sub">Status: <strong>' + statusLabel + '<\/strong> &nbsp;·&nbsp; Driver: <strong>' + (loadData.driver || '—') + '<\/strong><\/div><\/div>'
      + '<div class="dates">'
      + 'Departure: <strong>' + fmt(loadData.departure_date) + '<\/strong><br>'
      + 'Completed: <strong>' + fmt(loadData.completed_at) + '<\/strong><br>'
      + 'Created: <strong>' + fmt(loadData.created_at) + '<\/strong>'
      + '<\/div><\/div>\n'
      + '<div class="meta-grid">'
      + '<div class="meta-item"><label>Total Miles<\/label><span>' + (loadData.total_miles || 0).toLocaleString() + ' mi<\/span><\/div>'
      + '<div class="meta-item"><label>Vehicles<\/label><span>' + (loadData.cars || []).length + '<\/span><\/div>'
      + '<div class="meta-item"><label>Revenue<\/label><span>$' + money(revenue) + '<\/span><\/div>'
      + '<div class="meta-item"><label>Net Profit<\/label><span class="' + (profit >= 0 ? 'green' : 'red') + '">$' + money(profit) + '<\/span><\/div>'
      + '<\/div>\n'
      + '<div class="section-title">Vehicles &amp; Stops<\/div>\n'
      + carsHtml + '\n'
      + '<div class="section-title">Route<\/div>\n'
      + '<div id="map"><\/div>\n'
      + '<div class="section-title">Financial Summary<\/div>\n'
      + '<div class="fin-grid">'
      + '<div class="fin-item"><label>Revenue<\/label><span>$' + money(revenue) + '<\/span><\/div>'
      + '<div class="fin-item"><label>' + driverCostLabel + '<\/label><span>$' + money(driverCostVal) + '<\/span><\/div>'
      + '<div class="fin-item"><label>Additional Cost<\/label><span>$' + money(additionalCost) + '<\/span><\/div>'
      + '<div class="fin-item"><label>Net Profit<\/label><span class="' + (profit >= 0 ? 'green' : 'red') + '">$' + money(profit) + '<\/span><\/div>'
      + '<div class="fin-item"><label>Cost Per Mile<\/label><span>$' + money(loadData.cost_per_mile) + '<\/span><\/div>'
      + '<div class="fin-item"><label>Total Miles<\/label><span>' + (loadData.total_miles || 0).toLocaleString() + ' mi<\/span><\/div>'
      + '<\/div>\n'
      + '<script>\n'
      + 'var markersData = ' + markersJson + ';\n'
      + 'var polylineStr = ' + polylineJson + ';\n'
      + 'var startLoc = ' + startJson + ';\n'
      + 'var endLoc = ' + endJson + ';\n'
      + 'function initMap() {\n'
      + '  var map = new google.maps.Map(document.getElementById("map"), {\n'
      + '    center: { lat: ' + centerLat + ', lng: ' + centerLng + ' },\n'
      + '    zoom: ' + zoom + ',\n'
      + '    disableDefaultUI: true, gestureHandling: "none"\n'
      + '  });\n'
      + '  if (startLoc) new google.maps.Marker({ position: startLoc, map: map, icon: { path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#4CAF50", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 } });\n'
      + '  if (endLoc) new google.maps.Marker({ position: endLoc, map: map, icon: { path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#F44336", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 } });\n'
      + '  markersData.forEach(function(pos, i) {\n'
      + '    new google.maps.Marker({ position: pos, map: map,\n'
      + '      label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },\n'
      + '      icon: { path: google.maps.SymbolPath.CIRCLE, scale: 12, fillColor: "#007AFF", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, labelOrigin: new google.maps.Point(0, 0) }\n'
      + '    });\n'
      + '  });\n'
      + '  if (polylineStr) {\n'
      + '    var decoded = google.maps.geometry.encoding.decodePath(polylineStr);\n'
      + '    new google.maps.Polyline({ path: decoded, geodesic: true, strokeColor: "#007AFF", strokeOpacity: 0.8, strokeWeight: 3, map: map });\n'
      + '    var bounds = new google.maps.LatLngBounds();\n'
      + '    decoded.forEach(function(p) { bounds.extend(p); });\n'
      + '    map.fitBounds(bounds);\n'
      + '  }\n'
      + '  google.maps.event.addListenerOnce(map, "idle", function() { setTimeout(function() { window.print(); }, 500); });\n'
      + '}\n'
      + '<\/script>\n'
      + '<script src="https://maps.googleapis.com/maps/api/js?key=' + apiKey + '&libraries=geometry&callback=initMap" async defer><\/script>\n'
      + '<\/body>\n<\/html>';

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const handleDeleteLoad = async () => {
    try {
      StateManager.setLoading(true);
      await firebase.firestore().collection('shipping-loads').doc(loadId).delete();
      Store.invalidateShippingLoads(); // derived car shipping status reads this
      StateManager.setAlertAndOpen("Load deleted successfully", "success");
      if (onDeleted) onDeleted();
    } catch (error) {
      console.error('Error deleting load:', error);
      StateManager.setAlertAndOpen("Error deleting load", "error");
    } finally {
      StateManager.setLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 4, textAlign: 'center' }}>Loading...</Box>;
  }

  return (
    <Box sx={{ py: 4 }}>
      {/* Admin Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Load #{loadId}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Print load summary">
              <IconButton onClick={handlePrint} size="small">
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {StateManager.isBackoffice() &&
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                Delete Load
              </Button>
            }
          </Box>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={loadData?.status || ''}
                label="Status"
                onChange={(e) => handleUpdateField('status', e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="posted">Posted</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Created Date"
              value={loadData?.created_at ? new Date(loadData.created_at).toLocaleString() : 'N/A'}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Departure Date"
                value={loadData?.departure_date ? new Date(loadData.departure_date) : null}
                onChange={(date) => handleUpdateField('departure_date', date ? date.toISOString() : null)}
                slotProps={{ ...datePickerSlotProps, textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Completion Date"
                value={loadData?.completed_at ? new Date(loadData.completed_at) : null}
                onChange={(date) => handleUpdateField('completed_at', date ? date.toISOString() : null)}
                slotProps={{ ...datePickerSlotProps, textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Driver Pay Type Toggle */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">Driver Pay Type:</Typography>
              <ToggleButtonGroup
                value={loadData?.driver_pay_type || 'per_mile'}
                exclusive
                size="small"
                onChange={(e, val) => {
                  if (val) handleUpdateField('driver_pay_type', val);
                }}
              >
                <ToggleButton value="per_mile">Per Mile</ToggleButton>
                <ToggleButton value="hourly">Hourly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>

          {/* Per Mile: existing driver select */}
          {(!loadData?.driver_pay_type || loadData.driver_pay_type === 'per_mile') && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Driver</InputLabel>
                <Select
                  value={loadData?.driver ?? ''}
                  label="Driver"
                  onChange={async (e) => {
                    const selectedDriver = e.target.value;
                    const costPerMile = driverCostPerMile[selectedDriver] || 0.65;
                    try {
                      const updatedLoadData = { ...loadData, driver: selectedDriver, cost_per_mile: costPerMile };
                      setLoadData(updatedLoadData);
                      await handleUpdateField('driver', selectedDriver);
                    } catch (error) {
                      console.error('Error updating driver:', error);
                      setLoadData(loadData);
                      StateManager.setAlertAndOpen("Error updating driver", "error");
                    }
                  }}
                >
                  <MenuItem value="">Select Driver</MenuItem>
                  <MenuItem value="Scott Bivens">Scott Bivens</MenuItem>
                  <MenuItem value="Clarence Bowens">Clarence Bowens</MenuItem>
                  <MenuItem value="Kelly Lindberg - New">Kelly Lindberg</MenuItem>
                  <MenuItem value="Steve McIntosh">Steve McIntosh</MenuItem>
                  <MenuItem value="Porters">Porters</MenuItem>
                  <MenuItem value="Outside Vendor">Outside Vendor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Hourly: driver name + rate + hours */}
          {loadData?.driver_pay_type === 'hourly' && (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Driver Name"
                  value={loadData?.driver ?? ''}
                  onChange={(e) => handleUpdateField('driver', e.target.value)}
                  placeholder="Enter driver name"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Hourly Rate"
                  type="number"
                  value={loadData?.hourly_rate ?? ''}
                  onChange={(e) => handleUpdateField('hourly_rate', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Hours Worked"
                  type="number"
                  value={loadData?.hours_worked ?? ''}
                  onChange={(e) => handleUpdateField('hours_worked', parseFloat(e.target.value) || 0)}
                  InputProps={{ endAdornment: <InputAdornment position="end">hrs</InputAdornment> }}
                  placeholder="0"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Location"
              value={loadData?.start || ''}
              onChange={(e) => handleUpdateField('start', e.target.value)}
              placeholder="Enter start location (e.g., address, city, zip)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Location"
              value={loadData?.end || ''}
              onChange={(e) => handleUpdateField('end', e.target.value)}
              placeholder="Enter end location (e.g., address, city, zip)"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Checklists Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignmentIcon color="action" />
          <Typography variant="h6">Checklists</Typography>
        </Box>
        <Grid container spacing={2}>
          {['departure', 'return'].map((type) => {
            const data = type === 'departure' ? loadData?.departure_checklist : loadData?.return_checklist;
            const label = type === 'departure' ? 'Departure' : 'Return';
            const isComplete = data?.completed;
            const hasData = !!data;
            return (
              <Grid item xs={12} sm={6} key={type}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: isComplete ? 'success.main' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: isComplete ? 'success.50' : 'background.paper',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {label} Checklist
                    </Typography>
                    {isComplete ? (
                      <Chip
                        label="Completed"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                        sx={{ mt: 0.5 }}
                      />
                    ) : hasData ? (
                      <Chip label="In Progress" color="warning" size="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <Chip label="Not Started" size="small" sx={{ mt: 0.5 }} />
                    )}
                    {data?.completed_at && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {new Date(data.completed_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant={isComplete ? 'outlined' : 'contained'}
                    color={isComplete ? 'success' : 'primary'}
                    size="small"
                    onClick={() => setChecklistOpen(type)}
                  >
                    {isComplete ? 'View / Edit' : hasData ? 'Continue' : 'Start'}
                  </Button>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Vehicles Section */}
      <VehiclesSection
        loadData={loadData}
        onUpdateField={handleUpdateField}
        onRemoveCarOrStop={handleRemoveCarOrStop}
        onVehicleClick={handleVehicleClick}
        onAddStops={handleAddStops}
        onOpenOutsideCarForm={handleOpenOutsideCarForm}
        onDragEnd={handleDragEnd}
        updateLocalUI={handleUpdateOrderLocally}
      />

      {/* Route Section */}
      <RouteSection
        loadData={loadData}
        mapInstance={mapInstance}
        setMapInstance={setMapInstance}
        markers={markers}
        startLocation={startLocation}
        endLocation={endLocation}
        isRouteUpdating={isRouteUpdating}
      />

      {/* Financial Details Section */}
      {StateManager.isBackoffice() &&
        <FinancialDetails
          loadData={loadData}
          onUpdateField={handleUpdateField}
        />
      }

      {/* Load Selector Dialog */}
      <ModalStackProvider value={myZ}>
      <Dialog
        open={isLoadSelectorOpen}
        onClose={handleLoadSelectorClose}
        maxWidth="xl"
        fullWidth
        sx={{ zIndex: myZ }}
      >
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          <LoadSelector
            onLoadCreated={handleLoadSelected}
            initialSelection={{
              selectedCars: loadData?.cars?.filter(car => ['car_delivery', 'car_pickup'].includes(car.type)).map(car => car.id) || []
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLoadSelectorClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Outside Car Form Dialog */}
      <AddOutsideCarForm
        open={isAddOutsideCarFormOpen}
        onClose={handleCloseOutsideCarForm}
        onSubmit={handleOutsideCarSubmit}
      />

      <VehicleDetailsDialog
        open={isVehicleDialogOpen}
        onClose={() => {
          setIsVehicleDialogOpen(false);
          setSelectedVehicle(null);
        }}
        vehicle={selectedVehicle}
        loadId={loadId}
        onUpdateField={handleUpdateField}
        onAddressChange={handleAddressChange}
      />

      {/* Checklist Dialogs */}
      {['departure', 'return'].map((type) => (
        <ChecklistDialog
          key={type}
          open={checklistOpen === type}
          onClose={() => setChecklistOpen(null)}
          type={type}
          loadId={loadId}
          existingData={type === 'departure' ? loadData?.departure_checklist : loadData?.return_checklist}
          onSaved={(fieldName, data) => setLoadData(prev => ({ ...prev, [fieldName]: data }))}
        />
      ))}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        sx={{ zIndex: myZ }}
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Delete Load
          </Typography>
          <Typography>
            Are you sure you want to delete this load? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteLoad}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </ModalStackProvider>
    </Box>
  );
};

export default ShippingLoadContent;
